import { FormEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Typography, Grid, Box, Stack, Button } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { AuthorizationGuard, CardAttribute, ModalLayout } from '@/components'
import { Indicator } from '@/components/ui'
import {
  useMutationWrapper,
  usePostHoursReportApprove,
  usePostHoursReportDecline,
} from '@/hooks'
import {
  HoursReport as HoursReportType,
  HoursReportInvalidVariables,
  UserGroups,
} from '@/types'
import {
  HoursReportItem,
  HourReportParams,
} from '@/features/hoursReporting/HoursReport/HoursReportItem'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { HoursReportFormContainer } from '@/components/forms'
import { ControlledTextField } from '@/components/inputs'
import { getSpeciesWithResearchTypesString } from '@/utils'
import { InvalidDataInfo } from '@/features'
import { usePostHoursReportDisapprove } from '@/hooks/api/hoursReports/usePostHoursReportDisapprove'
import { Edit, XSquare } from 'react-feather'
import dayjs from 'dayjs'
import { DATE_FORMAT_DMY } from '@/constants'

export type HoursReportDetailsProps = {
  hoursReportData: HoursReportType
}

export type HoursReportStatusFormProps = {
  validation_message: string
}

export const HoursReport = ({
  hoursReportData,
}: HoursReportDetailsProps): JSX.Element => {
  const { t } = useTranslation('hoursReporting')
  const [data, setData] = useState<HoursReportType>(hoursReportData)

  const [isHoursReportEdit, setIsHoursReportEdit] = useState(false)

  const { mutateAsync: approveValidation, isLoading: approving } =
    useMutationWrapper(() =>
      usePostHoursReportApprove({ id: hoursReportData.id })
    )

  const { mutateAsync: declineValidation, isLoading: declining } =
    useMutationWrapper(() =>
      usePostHoursReportDecline({ id: hoursReportData.id })
    )

  const { mutateAsync: disapproveValidation, isLoading: disapproving } =
    useMutationWrapper(() =>
      usePostHoursReportDisapprove({ id: hoursReportData.id })
    )

  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = useForm<HoursReportStatusFormProps>({
    mode: 'onChange',
    defaultValues: {
      validation_message: '',
    },
    resolver: yupResolver(
      yup.object({
        validation_message: yup.string().required(),
      })
    ),
  })

  const handleApproveValidation = async (
    formData: HoursReportStatusFormProps
  ) => {
    const params = {
      validation_message: formData.validation_message,
    }

    await approveValidation(params, {
      successMessageKey: 'hoursReporting:notifications.approved',
      onSuccess: (data) => {
        setData(data)
      },
    })
  }

  const handleDeclineValidation = async (
    formData: HoursReportStatusFormProps
  ) => {
    const params = {
      validation_message: formData.validation_message,
    }

    await declineValidation(params, {
      successMessageKey: 'hoursReporting:notifications.declined',
      onSuccess: (data) => {
        setData(data)
      },
    })
  }

  const handleDisapprove = async () => {
    await disapproveValidation(undefined, {
      successMessageKey: 'hoursReporting:notifications.disapproved',
      onSuccess: (data) => {
        setData(data)
      },
    })
  }

  const onSubmitValidationForm = (e: FormEvent) => {
    const action = (
      e.nativeEvent as SubmitEvent
    ).submitter?.attributes.getNamedItem('name')?.value
    const handler =
      action === 'approve' ? handleApproveValidation : handleDeclineValidation

    handleSubmit(async (data) => handler(data))(e)
  }

  const handleEditObservationLog = () => {
    setIsHoursReportEdit(!isHoursReportEdit)
  }

  return (
    <ModalLayout title={t('reportDetailTitle', { id: data.id })}>
      <Grid container={true} rowSpacing={5} width={{ md: 925, sm: 'auto' }}>
        <>
          {!data ? (
            <Typography>{t('hours-report.noData')}</Typography>
          ) : (
            <>
              <Grid xs={12} item={true} container={true} spacing={4}>
                <Grid xs={3} item={true}>
                  <CardAttribute
                    title={t('hours-report.fieldTitles.projectNumber')}
                    value={
                      data.research_visit_event.research_area.project
                        .project_number
                    }
                  />
                </Grid>
                <Grid xs={6} item={true}>
                  <CardAttribute
                    title={t('hours-report.fieldTitles.projectName')}
                    value={data.research_visit_event.research_area.project.name}
                  />
                </Grid>
                <Grid xs={3} item={true}>
                  <CardAttribute
                    title={t('hours-report.fieldTitles.clientName')}
                    value={
                      data.research_visit_event.research_area.project
                        .client_name
                    }
                  />
                </Grid>

                <Grid xs={3} item={true}>
                  <CardAttribute
                    title={t('hours-report.fieldTitles.researchAreaNumber')}
                    value={data.research_visit_event.research_area.id}
                  />
                </Grid>
                <Grid xs={9} item={true}>
                  <CardAttribute
                    title={t('hours-report.fieldTitles.researchAreaLocation')}
                    value={
                      data.research_visit_event.research_area.location_name
                    }
                  />
                </Grid>
              </Grid>
              <Grid xs={12} item={true}>
                <CardAttribute
                  title={t('hours-report.fieldTitles.researcherAddress')}
                  value={data.researcher.address_name}
                />
              </Grid>
              <Grid xs={12} item={true} container={true} spacing={4}>
                <Grid xs={2} item={true}>
                  <CardAttribute
                    title={t('hours-report.fieldTitles.roundId')}
                    value={`${data.research_visit_event?.id}`}
                  />
                </Grid>
                <Grid xs={2} item={true}>
                  <CardAttribute
                    title={t('hours-report.fieldTitles.round')}
                    value={`${data.research_visit_event?.round_type ?? '-'}`}
                  />
                </Grid>
                <Grid xs={2} item={true}>
                  <CardAttribute
                    title={t('hours-report.fieldTitles.roundNumber')}
                    value={`${data.research_visit_event?.round_number ?? '-'}`}
                  />
                </Grid>
                <Grid xs={3} item={true}>
                  <CardAttribute
                    title={t('hours-report.fieldTitles.roundName')}
                    value={`${data.research_visit_event?.round_name ?? '-'}`}
                  />
                </Grid>
                <Grid xs={3} item={true}>
                  <CardAttribute
                    title={t('hours-report.fieldTitles.roundDate')}
                    value={`${
                      data.research_visit_event?.start_datetime
                        ? dayjs(
                            data.research_visit_event.start_datetime
                          ).format(DATE_FORMAT_DMY)
                        : '-'
                    }`}
                  />
                </Grid>
                <Grid xs={12} item={true}>
                  <CardAttribute
                    title={t('hours-report.fieldTitles.species')}
                    value={getSpeciesWithResearchTypesString(
                      data.research_visit_event
                    )}
                  />
                </Grid>
              </Grid>

              <Grid xs={12} item={true}>
                <Stack
                  direction="row"
                  alignItems="flex-start"
                  justifyContent="space-between"
                  spacing={4}
                  width="100%"
                  mt={6}
                >
                  <Typography variant="caption" color="grey.600">
                    {t('hours-report.fieldTitles.hoursReportData')}
                  </Typography>
                  <AuthorizationGuard
                    allowedGroups={[UserGroups.MANAGER]}
                    redirect={false}
                  >
                    <Button
                      onClick={handleEditObservationLog}
                      variant="textThin"
                      endIcon={
                        isHoursReportEdit ? (
                          <XSquare size={14} />
                        ) : (
                          <Edit size={14} />
                        )
                      }
                    >
                      {isHoursReportEdit
                        ? t('buttons.closeEditing')
                        : t('buttons.editHoursReport')}
                    </Button>
                  </AuthorizationGuard>
                </Stack>
              </Grid>

              {isHoursReportEdit ? (
                <HoursReportFormContainer
                  data={data}
                  setUpdatedHoursReport={setData}
                  onSuccessUpdate={() => setIsHoursReportEdit(false)}
                />
              ) : (
                <Grid xs={12} item={true} container={true}>
                  <Grid xs={12} item={true} container={true}>
                    <Grid xs={3} item={true}>
                      <CardAttribute
                        title={t(
                          'hours-report.fieldTitles.researchTimeMinutes'
                        )}
                        value={
                          <HoursReportItem
                            name={HourReportParams.RESEARCH_TIME}
                            value={data.research_time_minutes}
                            invalidVariables={data.invalid_variables}
                          />
                        }
                        mt={4}
                      />
                    </Grid>
                    {!!data.research_time_comment && (
                      <Grid xs={9} item={true}>
                        <CardAttribute
                          title={t(
                            'hours-report.fieldTitles.researchTimeComment'
                          )}
                          value={data.research_time_comment}
                          mt={4}
                        />
                      </Grid>
                    )}
                  </Grid>
                  <Grid xs={12} item={true} container={true}>
                    <Grid xs={3} item={true}>
                      <CardAttribute
                        title={t('hours-report.fieldTitles.travelTimeMinutes')}
                        value={
                          <HoursReportItem
                            name={HourReportParams.TRAVEL_TIME}
                            value={data.travel_time_minutes}
                            invalidVariables={data.invalid_variables}
                          />
                        }
                        mt={4}
                      />
                    </Grid>
                    {!!data.travel_time_comment && (
                      <Grid xs={9} item={true}>
                        <CardAttribute
                          title={t(
                            'hours-report.fieldTitles.travelTimeComment'
                          )}
                          value={data.travel_time_comment}
                          mt={4}
                        />
                      </Grid>
                    )}
                  </Grid>
                  <Grid xs={12} item={true} container={true}>
                    <Grid xs={3} item={true}>
                      <CardAttribute
                        title={t('hours-report.fieldTitles.travelDistanceKm')}
                        value={
                          <HoursReportItem
                            name={HourReportParams.TRAVEL_DISTANCE}
                            value={`${data.travel_distance_km} ${
                              data.researcher.has_travel_pass
                                ? '(' + t('travelPassHolder') + ')'
                                : ''
                            }`}
                            invalidVariables={data.invalid_variables}
                          />
                        }
                        mt={4}
                      />
                    </Grid>
                    {!!data.travel_distance_comment && (
                      <Grid xs={9} item={true}>
                        <CardAttribute
                          title={t(
                            'hours-report.fieldTitles.travelDistanceComment'
                          )}
                          value={data.travel_distance_comment}
                          mt={4}
                        />
                      </Grid>
                    )}
                  </Grid>
                  <Grid xs={3} item={true}>
                    <CardAttribute
                      title={t('hours-report.fieldTitles.reportedAsCoupled')}
                      value={
                        <Stack direction="row" alignItems="center">
                          {data.invalid_variables?.includes(
                            HoursReportInvalidVariables.IS_COUPLED_SWIFT_ROUND
                          ) && (
                            <InvalidDataInfo
                              title={t(
                                'hours-report.fieldTitles.invalidVariables'
                              )}
                              invalidData={[
                                t(
                                  `invalidVariable.${HoursReportInvalidVariables.IS_COUPLED_SWIFT_ROUND}`
                                ),
                              ]}
                            />
                          )}
                          <Typography
                            variant="body2"
                            color={
                              data.invalid_variables?.includes(
                                HoursReportInvalidVariables.IS_COUPLED_SWIFT_ROUND
                              )
                                ? 'error.main'
                                : 'primary.main'
                            }
                          >
                            {data.reported_as_coupled
                              ? t('hours-report.yes')
                              : t('hours-report.no')}
                          </Typography>
                        </Stack>
                      }
                      mt={4}
                    />
                  </Grid>
                  {data.reported_as_coupled && (
                    <Grid xs={3} item={true}>
                      <CardAttribute
                        title={t(
                          'hours-report.fieldTitles.coupledRoundCounter'
                        )}
                        value={data.coupled_round_counter}
                        mt={4}
                      />
                    </Grid>
                  )}
                  <Grid xs={3} item={true}>
                    <CardAttribute
                      title={t('hours-report.fieldTitles.carPooling')}
                      value={
                        data.car_pooling
                          ? t('hours-report.yes')
                          : t('hours-report.no')
                      }
                      mt={4}
                    />
                  </Grid>
                  <Grid xs={6} item={true}>
                    <CardAttribute
                      title={t(
                        'hours-report.fieldTitles.carPoolingResearchers'
                      )}
                      value={
                        data.car_pooling_researchers?.length
                          ? data.research_visit_event.researchers
                              .filter((r) =>
                                data.car_pooling_researchers?.includes(r.id)
                              )
                              .map((r) => `${r.first_name} ${r.last_name}`)
                              .join(', ')
                          : '-'
                      }
                      mt={4}
                    />
                  </Grid>

                  <Grid xs={12} item={true} container={true}>
                    <Grid xs={3} item={true}>
                      <CardAttribute
                        title={t(
                          'hours-report.fieldTitles.reportingTimeMinutes'
                        )}
                        value={
                          <HoursReportItem
                            name={HourReportParams.REPORTING_TIME}
                            value={data.reporting_time_minutes}
                            invalidVariables={data.invalid_variables}
                          />
                        }
                        mt={4}
                      />
                    </Grid>
                    {!!data.reporting_time_comment && (
                      <Grid xs={9} item={true}>
                        <CardAttribute
                          title={t(
                            'hours-report.fieldTitles.reportingTimeComment'
                          )}
                          value={data.reporting_time_comment}
                          mt={4}
                        />
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              )}

              <Grid xs={12} item={true} container={true}>
                <Grid xs={12} item={true}>
                  <Stack direction="row" gap={4} alignItems="center" mt={4}>
                    <CardAttribute
                      title={t('hours-report.fieldTitles.status')}
                      value={
                        <Indicator
                          isSmall={false}
                          status={data.status}
                          name={data.status}
                        />
                      }
                    />
                    {['approved', 'declined'].includes(data.status) && (
                      <Box>
                        <LoadingButton
                          variant="contained"
                          color="warning"
                          type="button"
                          loading={disapproving}
                          disableElevation={true}
                          disabled={disapproving}
                          onClick={handleDisapprove}
                        >
                          <span>
                            {t('hours-report.buttons.disapproveValidation')}
                          </span>
                        </LoadingButton>
                      </Box>
                    )}
                  </Stack>
                </Grid>
                {!!data.validation_message && (
                  <Grid xs={12} item={true}>
                    <CardAttribute
                      title={t('hours-report.fieldTitles.validationComment')}
                      value={data.validation_message}
                      mt={4}
                    />
                  </Grid>
                )}
                {data.status === 'invalid' ? (
                  <Box mt={4}>
                    <form onSubmit={onSubmitValidationForm}>
                      <Stack spacing={2} alignItems="end">
                        <Box width={600}>
                          <ControlledTextField
                            name="validation_message"
                            control={control}
                            placeholder={t('placeholders.validationComment')}
                            multiline={true}
                            rows={2}
                            inputProps={{
                              style: {
                                padding: 0,
                              },
                            }}
                          />
                        </Box>
                        <Stack direction="row" gap={4}>
                          <LoadingButton
                            variant="contained"
                            color="error"
                            type="submit"
                            name="decline"
                            loading={declining}
                            disableElevation={true}
                            disabled={!isDirty || approving || declining}
                          >
                            <span>
                              {t('hours-report.buttons.declineValidation')}
                            </span>
                          </LoadingButton>
                          <LoadingButton
                            variant="contained"
                            color="secondary"
                            type="submit"
                            name="approve"
                            loading={approving}
                            disableElevation={true}
                            disabled={!isDirty || approving || declining}
                          >
                            <span>
                              {t('hours-report.buttons.approveValidation')}
                            </span>
                          </LoadingButton>
                        </Stack>
                      </Stack>
                    </form>
                  </Box>
                ) : null}
                {data.status === 'valid' ? (
                  <Box mt={4}>
                    <form onSubmit={onSubmitValidationForm}>
                      <Stack spacing={2} alignItems="end">
                        <Box width={600}>
                          <ControlledTextField
                            name="validation_message"
                            control={control}
                            placeholder={t('placeholders.validationComment')}
                            multiline={true}
                            rows={2}
                            inputProps={{
                              style: {
                                padding: 0,
                              },
                            }}
                          />
                        </Box>
                        <Stack direction="row" gap={4}>
                          <LoadingButton
                            variant="contained"
                            color="error"
                            type="submit"
                            name="decline"
                            loading={declining}
                            disableElevation={true}
                            disabled={!isDirty || approving || declining}
                          >
                            <span>
                              {t('hours-report.buttons.declineValidation')}
                            </span>
                          </LoadingButton>
                        </Stack>
                      </Stack>
                    </form>
                  </Box>
                ) : null}
              </Grid>
            </>
          )}
        </>
      </Grid>
    </ModalLayout>
  )
}
